/**
=========================================================
* Trust Cargo - Royal Gold on Deep Navy
=========================================================

* Base palette for the whole system. Every component reads its colours from
* here, so this file is the single place the look is decided.
*
* The idea: deep maritime navy for structure and weight, muted antique gold
* for anything that deserves attention, warm ivory instead of cold grey so
* the screens feel printed rather than lit. Semantic colours (success /
* warning / error) stay recognisable but are desaturated to sit calmly
* beside the gold instead of fighting it.
*/

// --- brand anchors -------------------------------------------------------
const NAVY_DEEP = "#0B1B3A"; // sidebar, headings
const NAVY_MID = "#16305C"; // gradient partner
const NAVY_SOFT = "#1E3A6E"; // interactive navy
const GOLD = "#C9A227"; // the accent
const GOLD_DARK = "#A8871C"; // pressed / hover
const GOLD_LIGHT = "#E3C766"; // gradient partner
const IVORY = "#F7F4EE"; // page background

const colors = {
  background: {
    default: IVORY,
  },

  text: {
    main: "#5B6780",
    focus: "#3D4759",
  },

  transparent: {
    main: "transparent",
  },

  white: {
    main: "#ffffff",
    focus: "#ffffff",
  },

  black: {
    light: "#0d1117",
    main: "#000000",
    focus: "#000000",
  },

  // primary is the gold - buttons, active states, anything to look expensive
  primary: {
    main: GOLD,
    focus: GOLD_DARK,
  },

  secondary: {
    main: "#4A5A7A",
    focus: "#5C6E90",
  },

  info: {
    main: NAVY_SOFT,
    focus: "#2A4E8F",
  },

  success: {
    main: "#1E8E5A",
    focus: "#26A96B",
  },

  warning: {
    main: "#D99A16",
    focus: "#E8AC2E",
  },

  error: {
    main: "#C0392B",
    focus: "#A93226",
  },

  light: {
    main: "#EFEAE0",
    focus: "#F3EFE7",
  },

  dark: {
    main: NAVY_DEEP,
    focus: NAVY_MID,
  },

  // warm greys - tinted towards the ivory so nothing looks blue-cold
  grey: {
    100: "#FAF8F4",
    200: "#EFEAE0",
    300: "#DFD8C9",
    400: "#C6BDAB",
    500: "#A0987F",
    600: "#7B7460",
    700: "#565244",
    800: "#37342C",
    900: "#1C1A16",
  },

  gradients: {
    primary: {
      main: GOLD_DARK,
      state: GOLD_LIGHT,
    },

    secondary: {
      main: "#4A5A7A",
      state: "#7C8AA8",
    },

    info: {
      main: NAVY_DEEP,
      state: NAVY_SOFT,
    },

    success: {
      main: "#147A4A",
      state: "#26A96B",
    },

    warning: {
      main: "#B87A0E",
      state: GOLD_LIGHT,
    },

    error: {
      main: "#8E2A20",
      state: "#C0392B",
    },

    light: {
      main: "#E4DDCE",
      state: "#FBF9F5",
    },

    dark: {
      main: "#060E1F",
      state: NAVY_MID,
    },
  },

  socialMediaColors: {
    facebook: {
      main: "#3b5998",
      dark: "#344e86",
    },

    twitter: {
      main: "#55acee",
      dark: "#3ea1ec",
    },

    instagram: {
      main: "#125688",
      dark: "#0e456d",
    },

    linkedin: {
      main: "#0077b5",
      dark: "#00669c",
    },

    pinterest: {
      main: "#cc2127",
      dark: "#b21d22",
    },

    youtube: {
      main: "#e52d27",
      dark: "#d41f1a",
    },

    vimeo: {
      main: "#1ab7ea",
      dark: "#13a3d2",
    },

    slack: {
      main: "#3aaf85",
      dark: "#329874",
    },

    dribbble: {
      main: "#ea4c89",
      dark: "#e73177",
    },

    github: {
      main: "#24292e",
      dark: "#171a1d",
    },

    reddit: {
      main: "#ff4500",
      dark: "#e03d00",
    },

    tumblr: {
      main: "#35465c",
      dark: "#2a3749",
    },
  },

  alertColors: {
    primary: {
      main: GOLD_DARK,
      state: GOLD_LIGHT,
      border: "#F0E2B8",
    },

    secondary: {
      main: "#4A5A7A",
      state: "#7C8AA8",
      border: "#D6DBE5",
    },

    info: {
      main: NAVY_DEEP,
      state: NAVY_SOFT,
      border: "#C3CEE2",
    },

    success: {
      main: "#147A4A",
      state: "#26A96B",
      border: "#BFE6D2",
    },

    warning: {
      main: "#B87A0E",
      state: "#E8AC2E",
      border: "#F5E3B7",
    },

    error: {
      main: "#8E2A20",
      state: "#C0392B",
      border: "#EEC2BD",
    },

    light: {
      main: "#DFD8C9",
      state: "#F3EFE7",
      border: "#FAF8F4",
    },

    dark: {
      main: "#060E1F",
      state: NAVY_MID,
      border: "#B9C1D1",
    },
  },

  badgeColors: {
    primary: {
      background: "#F6EBC4",
      text: "#8A6E12",
    },

    secondary: {
      background: "#E2E6EE",
      text: "#44536F",
    },

    info: {
      background: "#D5DFF2",
      text: NAVY_SOFT,
    },

    success: {
      background: "#CDEBDB",
      text: "#126B47",
    },

    warning: {
      background: "#FAEBC8",
      text: "#9A6D0C",
    },

    error: {
      background: "#F5D5D1",
      text: "#8E2A20",
    },

    light: {
      background: "#ffffff",
      text: "#C6BDAB",
    },

    dark: {
      background: "#8FA0BE",
      text: "#0B1B3A",
    },
  },

  inputColors: {
    borderColor: { main: "#DFD8C9", focus: GOLD },
    boxShadow: GOLD_LIGHT,
    error: "#C0392B",
    success: "#1E8E5A",
  },

  sliderColors: {
    thumb: { borderColor: "#DFD8C9" },
  },

  circleSliderColors: {
    background: "#DFD8C9",
  },

  tabs: {
    indicator: { boxShadow: "#DFD8C9" },
  },
};

export default colors;
